import { BackButton, Breadcrumbs, Loading, Wrapper } from "@networkraildigitalfactory/components";
import React, { useEffect, useMemo, useState } from "react";
import { Redirect, useHistory, useParams } from "react-router-dom";
import { usePDP, useUsers } from "../../hooks/useAPI";
import { auditHistoryTextReplacer, getEmployeeEmailByPathname } from "../../services/utils";
import AuditTrail from "../AuditTrail/AuditTrail";
import CommentList from "../CommentList/CommentList";
import CreateComment from "../CreateComment/CreateComment";
import { AccessDeniedModal, AreYouSureModal, InputModal } from "../Modals";
import StatusLabel from "../StatusLabel/StatusLabel";
import ActionButtons from "../ViewObjective/ActionButtons";
import Message from "../ViewObjective/Message";
import styles from "../ViewObjective/ViewObjective.module.css";
import ViewPdpData from "../ViewPdpData/ViewPdpData";

const ViewPDP = (props) => {
  const { user, isDirtyForm, setIsDirtyForm, setNavModalProps } = props;

  const params = useParams();
  const history = useHistory();
  const [isLoading, setLoading] = useState(false);
  const [toggleAccessModal, setToggleAccessModal] = useState(false);

  const [
    { data: rawDevelopmentGoal, loading: developmentGoalLoading, error: getError },
    getDevelopmentGoal,
  ] = usePDP(params.id);

  const developmentGoal = useMemo(
    () => auditHistoryTextReplacer(rawDevelopmentGoal, "Vision statement", "GBRTT behaviour"),
    [rawDevelopmentGoal]
  );

  const navTo = (dest) => history.push(dest);

  let isLineManager = localStorage.getItem("isLineManager");
  if (isLineManager && developmentGoal && developmentGoal.email !== user.email) {
    isLineManager = true;
  } else {
    isLineManager = false;
  }

  const openAccessModal = () => {
    setToggleAccessModal(true);
  };

  const onCloseAccessModal = () => {
    setToggleAccessModal(false);
    navTo("/my-team/");
  };

  const displayAccessModal = (postError) => {
    if (postError && postError.toString().includes("401")) {
      openAccessModal();
    }
  };

  useEffect(() => {
    displayAccessModal(getError);
  }, [getError]);

  const fetchData = async (data) => {
    try {
      setLoading(true);
      await getDevelopmentGoal(data);
    } catch (error) {
      console.error("Failed to fetch data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const email = getEmployeeEmailByPathname(history.location.pathname, "/view-development-goal");
  const [{ data: employeeBeingViewed }] = useUsers(email);
  let fullName;
  if (employeeBeingViewed) {
    // eslint-disable-next-line prefer-destructuring
    fullName = employeeBeingViewed.fullName;
  }

  const [isInputModalOpen, setIsInputModalOpen] = useState(false);
  const [isAreYouSureModalOpen, setIsAreYouSureModalOpen] = useState(false);
  const [modalAction, setModalAction] = useState("");

  const toggleInputModal = (action) => {
    setIsInputModalOpen(!isInputModalOpen);
    if (!isInputModalOpen) {
      setModalAction(action);
    }
  };

  const toggleAreYouSureModal = (action) => {
    setIsAreYouSureModalOpen(!isAreYouSureModalOpen);
    if (!isAreYouSureModalOpen) {
      setModalAction(action);
    }
  };

  const onBackClick = () => {
    history.push(isLineManager ? `/my-team/${email}/PDP` : "/my-objectives/PDP");
  };

  const goToEditView = () => {
    if (isDirtyForm) {
      setNavModalProps({ show: true, navPath: `/edit-development-goal/${developmentGoal._id}` });
    } else {
      history.push(`/edit-development-goal/${developmentGoal._id}`);
    }
  };

  return (
    <>
      <Loading loading={isLoading} />
      {fullName !== "" ? (
        <>
          <div className={styles.breadcrumbs_wrapper}>
            <Breadcrumbs
              items={
                isLineManager
                  ? [
                      {
                        text: "My Team",
                        onClick: () => {
                          if (isDirtyForm) {
                            setNavModalProps({ show: true, navPath: "/my-team" });
                          } else {
                            navTo("/my-team");
                          }
                        },
                      },
                      {
                        text: fullName,
                        onClick: () => {
                          if (isDirtyForm) {
                            setNavModalProps({ show: true, navPath: `/my-team/${email}/PDP` });
                          } else {
                            navTo(`/my-team/${email}/PDP`);
                          }
                        },
                      },
                      { text: developmentGoal && developmentGoal.name },
                    ]
                  : [
                      {
                        text: "My Objectives",
                        onClick: () => {
                          if (isDirtyForm) {
                            setNavModalProps({ show: true, navPath: "/my-objectives/PDP" });
                          } else {
                            navTo("/my-objectives/PDP");
                          }
                        },
                      },
                      { text: developmentGoal && developmentGoal.name },
                    ]
              }
            />
          </div>
          {!developmentGoalLoading && !getError && !isLoading && (
            <>
              <Wrapper maxWidth={1350} align="center">
                <div className={styles.row}>
                  <BackButton
                    text={`back to ${isLineManager ? "" : "my "}development goals`}
                    onClick={() => {
                      if (isDirtyForm) {
                        setNavModalProps({
                          show: true,
                          navPath: isLineManager ? `/my-team/${email}/PDP` : "/my-objectives/PDP",
                        });
                      } else {
                        onBackClick();
                      }
                    }}
                    style={{ lineHeight: "140%", letterSpacing: "0.0015em" }}
                  />
                  <StatusLabel
                    status={
                      developmentGoal.status.charAt(0).toUpperCase() +
                      developmentGoal.status.slice(1)
                    }
                  />
                </div>
                <div className={styles.row} style={{ paddingTop: 0 }}>
                  <Message
                    status={developmentGoal.status}
                    isLineManager={isLineManager}
                    type="PDP"
                  />
                </div>
                <div className={styles.light_bg}>
                  <ViewPdpData
                    data={developmentGoal}
                    isLineManager={isLineManager}
                    user={user}
                    employeeBeingViewed={isLineManager ? employeeBeingViewed : null}
                  />
                </div>
                <ActionButtons
                  isLineManager={isLineManager}
                  status={developmentGoal.status}
                  toggleInputModal={toggleInputModal}
                  toggleAreYouSureModal={toggleAreYouSureModal}
                  goToEditView={goToEditView}
                  type="PDP"
                />
                <hr />
                <CreateComment
                  pdpId={developmentGoal._id}
                  user={user}
                  isLineManager={isLineManager}
                  getUpdatedPdp={fetchData}
                  type="PDP"
                  setIsDirtyForm={setIsDirtyForm}
                />
                <CommentList
                  data={developmentGoal.comments}
                  user={user}
                  isLineManager={isLineManager}
                  type="comment"
                />
                <AuditTrail data={developmentGoal.auditHistory} />
              </Wrapper>
              <InputModal
                show={isInputModalOpen}
                isLineManager={isLineManager}
                close={toggleInputModal}
                confirm={toggleInputModal}
                action={modalAction}
                objectiveId={developmentGoal._id}
                redirect={`/my-team/${email}/PDP`}
                type="PDP"
              />
              <AreYouSureModal
                show={isAreYouSureModalOpen}
                close={toggleAreYouSureModal}
                confirm={toggleAreYouSureModal}
                action={modalAction}
                selectedId={developmentGoal._id}
                redirect={`/my-team/${email}/PDP`}
                type="PDP"
              />
            </>
          )}
          <AccessDeniedModal show={toggleAccessModal} close={onCloseAccessModal} />
        </>
      ) : (
        <Redirect to="/" />
      )}
    </>
  );
};

export default ViewPDP;
